import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { Download, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
  MasterSummaryStrip,
  useMasterListShortcuts,
} from "@/components/shared/master-shell";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/input";
import {
  useBulkSetVendorActive,
  useDeleteVendor,
  useVendors,
} from "@/features/vendors/api";
import { VENDOR_TYPES, formatMoney, labelFor } from "@/features/vendors/constants";
import type { VendorListRow } from "@/features/vendors/types";
import { isSupabaseConfigured } from "@/lib/supabase";

const columnHelper = createColumnHelper<VendorListRow>();

export function VendorsPage() {
  return (
    <ErrorBoundary title="Vendors page crashed">
      <VendorsPageInner />
    </ErrorBoundary>
  );
}

function VendorsPageInner() {
  const navigate = useNavigate();
  const { data = [], isLoading, error, refetch, isFetching } = useVendors();
  const deleteVendor = useDeleteVendor();
  const bulkActive = useBulkSetVendorActive();

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleting, setDeleting] = useState<VendorListRow | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const counts = useMemo(
    () => ({
      all: data.length,
      active: data.filter((v) => v.is_active).length,
      inactive: data.filter((v) => !v.is_active).length,
    }),
    [data],
  );

  const filtered = useMemo(
    () =>
      data.filter((v) => {
        if (statusFilter === "active" && !v.is_active) return false;
        if (statusFilter === "inactive" && v.is_active) return false;
        if (typeFilter !== "all" && v.vendor_type !== typeFilter) return false;
        return true;
      }),
    [data, statusFilter, typeFilter],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 32,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(Boolean(v))}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(Boolean(v))}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor("name", {
        header: "Vendor",
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium leading-tight">{row.original.name}</div>
            <div className="truncate font-mono text-2xs text-muted-foreground">
              {row.original.code}
              {row.original.city ? ` · ${row.original.city}` : ""}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("vendor_type", {
        header: "Type",
        cell: (info) => <Badge variant="outline">{labelFor(VENDOR_TYPES, info.getValue())}</Badge>,
      }),
      columnHelper.display({
        id: "contact",
        header: "Primary contact",
        cell: ({ row }) => {
          const c = row.original.primary_contact;
          if (!c) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="min-w-0">
              <div className="truncate leading-tight">{c.name}</div>
              <div className="truncate text-2xs text-muted-foreground">
                {c.mobile || c.email || "—"}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("batch_prefix", {
        header: "Batch",
        cell: (info) => <span className="font-mono text-xs">{info.getValue()}</span>,
      }),
      columnHelper.accessor("outstanding_balance", {
        header: "Outstanding",
        cell: (info) => {
          const amount = Number(info.getValue()) || 0;
          return amount ? (
            <span className="tabular-nums">{formatMoney(amount)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      }),
      columnHelper.accessor("opening_balance", {
        header: "Opening",
        cell: ({ row }) => {
          const amount = Number(row.original.opening_balance) || 0;
          const asOf = row.original.opening_balance_as_of;
          if (!amount && !asOf) return <span className="text-muted-foreground">—</span>;
          return (
            <div className="tabular-nums">
              <div>{formatMoney(amount)}</div>
              {asOf ? (
                <div className="text-2xs text-muted-foreground">
                  {format(new Date(asOf), "dd MMM yyyy")}
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor("is_active", {
        header: "Status",
        cell: (info) => (
          <StatusDot tone={info.getValue() ? "success" : "neutral"}>
            {info.getValue() ? "Active" : "Inactive"}
          </StatusDot>
        ),
      }),
      columnHelper.display({
        id: "actions",
        size: 40,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleting(row.original);
            }}
          >
            <Trash2 />
          </Button>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, globalFilter, rowSelection },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const empty = !isLoading && !error && data.length === 0;

  const createVendor = useCallback(() => {
    void navigate({ to: "/vendors/new" });
  }, [navigate]);

  useMasterListShortcuts({
    searchInputId: "vendors-search",
    onCreate: createVendor,
  });

  function exportCsv() {
    const list = table.getFilteredRowModel().rows.map((r) => r.original);
    const header = ["code", "name", "type", "gstin", "city", "state", "batch_prefix", "opening_balance", "outstanding", "active"];
    const body = list.map((v) =>
      [
        v.code,
        csv(v.name),
        v.vendor_type,
        v.gstin ?? "",
        csv(v.city),
        csv(v.state),
        v.batch_prefix,
        String(v.opening_balance ?? 0),
        String(v.outstanding_balance ?? 0),
        v.is_active ? "1" : "0",
      ].join(","),
    );
    const blob = new Blob([[header.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendors-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Vendors"
        countLabel={`${counts.all} records`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
              <Download />
              Export
            </Button>
            <Button type="button" size="sm" onClick={createVendor}>
              <Plus />
              New vendor
            </Button>
          </>
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase environment variables to enable live data.</MasterBanner>
      ) : null}

      {error ? <MasterBanner tone="danger">{(error as Error).message}</MasterBanner> : null}

      {empty ? (
        <EmptyState
          title="No vendors yet"
          description="Add suppliers to raise purchase orders and receive stock."
          action={
            <Button type="button" size="sm" onClick={createVendor}>
              <Plus />
              Create vendor
            </Button>
          }
        />
      ) : (
        <>
          <MasterSummaryStrip
            items={[
              { label: "Total", value: counts.all },
              { label: "Active", value: counts.active, tone: "success" },
              { label: "Inactive", value: counts.inactive },
            ]}
          />

          <MasterFilterBar shownLabel={`${table.getFilteredRowModel().rows.length} shown`}>
            <MasterSearchInput
              id="vendors-search"
              value={globalFilter}
              onChange={setGlobalFilter}
              placeholder="Search vendor, code, GSTIN…  (/)"
            />
            <NativeSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="w-36"
            >
              <option value="active">Active ({counts.active})</option>
              <option value="inactive">Inactive ({counts.inactive})</option>
              <option value="all">All ({counts.all})</option>
            </NativeSelect>
            <NativeSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-36">
              <option value="all">All types</option>
              {VENDOR_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
          </MasterFilterBar>

          <DataTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyTitle="No matching vendors"
            onRowClick={(row) =>
              void navigate({
                to: "/vendors/$vendorId/profile",
                params: { vendorId: row.original.id },
              })
            }
          />
        </>
      )}

      {selectedIds.length > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 flex justify-center pb-4">
          <div className="flex items-center gap-2 rounded-full border border-border-strong bg-popover px-3 py-1.5 shadow-lg">
            <span className="text-xs tabular">{selectedIds.length} selected</span>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() =>
                void bulkActive.mutateAsync({ ids: selectedIds, is_active: true }).then(() => setRowSelection({}))
              }
            >
              Activate
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() =>
                void bulkActive.mutateAsync({ ids: selectedIds, is_active: false }).then(() => setRowSelection({}))
              }
            >
              Deactivate
            </Button>
            <Button type="button" size="xs" variant="ghost" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete vendor?"
        description={
          deleting
            ? `${deleting.name} and its contacts, addresses and notes will be removed.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={deleteVendor.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteVendor.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </MasterPage>
  );
}

function csv(value: string) {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
